<template>
  <li class="list-reset my1" :class="{ embed: isEmbed }">
    <div class="px2 mb1 h6 flex justify-between items-end">
      <span>
        <router-link :to="`/user/${note.author}/${note.author_name}`"><strong>{{ note.author_name }}</strong></router-link><br/>
        <span class="gray" v-if="locationText">{{ locationText }}</span>
      </span>
      <div v-if="!isEmbed" class="relative">
        <a v-on:click="showMenu = !showMenu" class="pointer outline-none silver" tabindex="0"><dot-icon/></a>
        <transition name="fade">
          <div v-if="showMenu" :key="note.id" class="absolute bg-white py2 pl2 pr1 right-0 top-100 nowrap z1 mxn2">
            <ul class="list-reset right-align">
              <li class="p1 mb0">
                <a v-if="canShare" class="pointer block" @click="shareItem">Share</a>
                <a v-else class="pointer block" :href="shareUrl" target="_blank">Permalink</a>
                <span @click="copyUrl()" class="pointer block mt2 relative" target="_blank">
                  Copy Embed Code
                  <transition name="copied" v-on:after-enter="copied = false">
                    <span v-if="copied" ref="copied" class="copied">Copied</span>
                  </transition>
                </span>
                <textarea ref="copyurl" class="copyurl">&lt;iframe src="embedUrl"/&gt;</textarea>
              </li>
              <li v-if="isMe(note.author)" class="p1 mb0">
                <a @click="deleteNote(note.id)" class="pointer">Delete</a>
              </li>
            </ul>
          </div>
        </transition>
      </div>
    </div>
    <div class="note-format mb2" :class="blockClass">
      <figure v-if="hasMedia" :class="blockClass" class="mb3">
        <div v-if="isImage" class="relative col-12" :style="'padding-top: ' + aspectRatioPercent">
          <img
            v-lazy="media.sizes.medium"
            :data-srcset="srcset"
            class="absolute top-0 right-0 bottom-0 left-0"
            />
        </div>
        <video v-else-if="isVideo" :src="media.url + '#t=0.01'" controls playsinline loading="lazy" loop></video>
        <audio v-else-if="isAudio" :src="media.url" controls loading="lazy"></audio>
        <figcaption @click="checkExpand" :class="{ expanded, pointer: expandable }" v-html="content" class="px2"></figcaption>
      </figure>

      <div v-else @click="checkExpand" v-html="content"/>

      <!-- taxonomies -->
      <div v-if="shownTags.length && !hideTags" class="tags px2">
        <template v-for="(tag, i) in shownTags">
          <tag-item :tag="tag" :key="i"/>
          <span :key="i + '-space'">&ensp;</span>
        </template>
        <span v-if="expandableTags" @click="expandedTags = true" class="gray pointer">more...</span>
      </div>
      <div v-if="isEmbed" class="px2 mt2"><a class="h6" href="/">More Field Notes ⤴</a></div>
    </div>
  </li>
</template>

<script>
  import { mapActions } from 'vuex'
  import { distanceInWordsToNow } from 'date-fns'
  import TagItem from '@/components/TagItem'
  import Autolinker from 'autolinker'

  export default {
    name: 'Preview',
    data () {
      return {
        expandable: false,
        expanded: false,
        showMenu: false,
        expandedTags: false,
        copied: false
      }
    },
    props: {
      note: Object,
      hideTags: Boolean,
      singleView: Boolean
    },
    computed: {
      dateCreated () {
        const created = new Date(this.note.date).getTime()
        return `${distanceInWordsToNow(
          new Date(created)
        )} ago`
      },
      locationText () {
        if (!this.note || !this.note.acf) return null

        if (this.note.acf.location && this.note.acf.location !== 'undefined') {
          return this.note.acf.location
        } else {
          return null
        }
      },
      aspectRatioPercent () {
        const m = this.note.acf.media

        if(!m) return 0

        const w = m.width
        const h = m.height

        if (!w || !h) return 0

        const a = h / w
        return (a * 100) + '%'
      },
      media () {
        return (this.note.acf && this.note.acf.media) || {}
      },
      isVideo () {
        return this.media.type === 'video'
      },
      isAudio () {
        return this.media.type === 'audio'
      },
      isImage () {
        return this.media.type === 'image'
      },
      hasMedia () {
        return this.isVideo || this.isAudio || this.isImage
      },
      srcset () {
        if (!this.isImage) return
        const { sizes } = this.media

        let srcset = []

        srcset.push(`${sizes.large} ${sizes['large-width']}w`)
        srcset.push(`${sizes.medium_large} ${sizes['medium_large-width']}w`)
        srcset.push(`${sizes.medium} ${sizes['medium-width']}w`)
        // srcset.push(`${sizes.thumbnail} ${sizes['thumbnail-width']}w`)

        return srcset.join(', ')
      },
      blockClass () {
        return `wp-block-${this.media.type || 'text'} ${this.expanded ? 'expanded' : ''}`
      },
      text () {
        return this.note.acf.text || this.note.content.rendered || ''
      },
      content () {
        const readMore = '<a class="h5 inline-block pointer read-more mt2">Read more &ldca;</a>'
        const readLess = this.singleView ? '' : '<a class="h5 block pointer read-less mt2 gray">Close &times;</a>'
        const exp = this.expandable && !this.expanded ? readMore : this.expanded ? readLess : ''
        const text = `<div class="note-content">${Autolinker.link(this.text)}</div>`
        const mod = `<p class="gray date">${this.dateCreated}&nbsp;</p>`
        let insertAt
        if (!text) {
          return mod
        }
        if (!this.hasMedia) {
          insertAt = text.length
        } else {
          insertAt = 0
        }
        if (insertAt > -1) {
          let html = text.slice(0, insertAt)
          if (!this.hasMedia) html += exp
          html += mod + text.slice(insertAt)
          return html
        }
        return text
      },
      canShare () {
        return typeof window.navigator.share === 'function'
      },
      shareUrl () {
        return `${window.location.origin}/id/${this.note.id}`
      },
      embedUrl () {
        return `${window.location.origin}/id/${this.note.id}?embed=1`
      },
      terms () {
        if (!this.note._embedded) return []
        return this.note._embedded['wp:term'] || []
      },
      methods () {
        let l = this.terms.filter((list) => {
          return list[0] && list[0].taxonomy === 'method'
        })
        return l[0] || this.note.method || []
      },
      keywords () {
        let l = this.terms.filter((list) => {
          return list[0] && list[0].taxonomy === 'keyword'
        })
        return l[0] || this.note.keyword || []
      },
      folksonomies () {
        let l = this.terms.filter((list) => {
          return list[0] && list[0].taxonomy === 'folksonomy'
        })
        return l[0] || this.note.folksonomy || []
      },
      projects () {
        return this.note.acf.linked_projects || []
      },
      allTags () {
        return [
          ...this.projects,
          ...this.methods,
          ...this.keywords,
          ...this.folksonomies
        ]
      },
      shownTags () {
        return this.expandedTags ? this.allTags : this.allTags.slice(0, 3)
      },
      expandableTags () {
        return this.shownTags.length < this.allTags.length
      }
    },
    methods: {
      copyUrl () {
        this.$refs.copyurl.select()
        document.execCommand('copy')
        this.copied = true
      },
      calcExpansion () {
        if (this.singleView) return

        const el = this.hasMedia ? this.$el.getElementsByTagName('figcaption')[0]
          : this.$el.getElementsByClassName('note-content')[0]
        if (!el) return
        const { clientHeight, scrollHeight } = el
        if (scrollHeight > clientHeight) {
          this.expandable = true
        }
      },
      checkExpand ({ target }) {
        if (!this.expandable) return

        // don't toggle if selecting text
        if (typeof window.getSelection !== 'undefined' &&
          window.getSelection().type === 'Range') return

        if (this.hasMedia) {
          this.expanded = !this.expanded
        } else if (target.nodeName === 'A') {
          this.expanded = target.classList.contains('read-more')

          if (!this.expanded) {
            setTimeout(() => {
              this.$el.scrollIntoView({
                block: 'center'
              })
            }, 10)
          }
        }
      },
      shareItem () {
        if (this.canShare) {
          window.navigator.share({
            url: this.shareUrl
          })
        }
      },
      isMe (authorId) {
        if (this.$store.state.userInfo === null) {
          return false
        }

        const myId = this.$store.state.userInfo.id
        const aId = parseInt(authorId)
        return aId === myId
      },
      ...mapActions(['deleteNote'])
    },
    created () {
      if (this.singleView) {
        this.expanded = true
      }
    },
    mounted () {
      this.calcExpansion()
    },
    components: { TagItem }
  }
</script>

<style lang="css" scoped>
  .embed {
    padding: 0;
    .note-format {
      padding: 0;
    }
  }
  .copyurl {
    position: absolute;
    right: 200vw;
  }
  .copied {
    position: absolute;
    right: 0;
    pointer-events: none;
  }
</style>
